import { useIntl } from '@finn/ui-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FilterKey, FiltersResponse } from '../../../core';
import { FilterName } from '../../../core/helpers/filters';
import { FiltersState } from '../../../filters-management';
import { TestProps } from '../../helpers';
import { useTrackFilterCategoryClicked } from '../../tracking';
import { FilterAccordion } from '../atoms/FilterAccordion';
import { RangeSelector } from '../atoms/RangeSelector';
import { FilterOptionClickFunction } from '../grouped-filters/FiltersGroup';

type Props = TestProps & {
  filterName: string;
  filterKey: [FilterKey, FilterKey];
  availableRange: [number, number];
  availableFilters?: FiltersResponse;
  selectedFilters: FiltersState;
  onClick: FilterOptionClickFunction;
  disabled?: boolean;
  testId: [string, string, string];
};

export const RangeFilter = ({
  filterName,
  filterKey,
  availableRange,
  availableFilters,
  selectedFilters,
  onClick,
  testId,
  disabled = false,
}: Props) => {
  const i18n = useIntl();
  const [expanded, setExpanded] = useState(false);
  const trackFilterOpen = useTrackFilterCategoryClicked(filterName);

  useEffect(() => {
    if (filterName === FilterName.EV_RANGE && availableFilters) {
      if (disabled) {
        setExpanded(false);
        onClick({
          [FilterKey.MIN_EV_RANGE]: undefined,
          [FilterKey.MAX_EV_RANGE]: undefined,
        });
      }
    }
  }, [availableFilters]);

  const minFilterName = `min_${filterName}` as keyof FiltersState;
  const maxFilterName = `max_${filterName}` as keyof FiltersState;

  const selectedRange = useMemo(
    () => [selectedFilters[minFilterName], selectedFilters[maxFilterName]],
    [selectedFilters[minFilterName], selectedFilters[maxFilterName]]
  );

  const isRangeSelected = useMemo(() => {
    return selectedRange.some((range) => range !== undefined);
  }, [selectedRange]);

  const handleAccordionClick = useCallback(() => {
    setExpanded(!expanded);
  }, [setExpanded, expanded]);

  const handleOpen = () => {
    trackFilterOpen;
    if (filterName === FilterName.EV_RANGE) {
      const filtersGroup = document.getElementById('filters-group');
      filtersGroup?.addEventListener(
        'transitionend',
        () => {
          filtersGroup.scrollTop = filtersGroup.scrollHeight;
        },
        { once: true }
      );
    }
  };

  return (
    <FilterAccordion
      title={i18n.formatMessage(`plp.filters.${filterName}.title`)}
      count={isRangeSelected ? 1 : undefined}
      onOpen={handleOpen}
      onClick={handleAccordionClick}
      expanded={expanded}
      testId={testId[0]}
      disabled={disabled}
    >
      <RangeSelector
        selectedRange={selectedRange as [number, number]}
        availableRange={[availableRange[0], availableRange[1]]}
        isRangeSelected={isRangeSelected}
        onRangeSelection={onClick}
        minRangeKey={filterKey[0]}
        maxRangeKey={filterKey[1]}
        suffix={` ${i18n.formatMessage(`plp.filters.${filterName}.suffix`)}`}
        minRangeLabel={i18n.formatMessage('plp.min')}
        maxRangeLabel={i18n.formatMessage('plp.max')}
        testIdMin={testId[1]}
        testIdMax={testId[2]}
      />
    </FilterAccordion>
  );
};
